import { useTranslation } from 'react-i18next';
import * as React from 'react';
import {
  OnSortChangedParams,
  SortDirection,
} from '@bb-ui/react-library/dist/components/SortableTable/SortableTable.types';
import { get, orderBy } from 'lodash';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { TableHead } from '@bb-ui/react-library/dist/components/TableHead';
import { TableRow } from '@bb-ui/react-library/dist/components/TableRow';
import { SortableTableHeaderCell } from '@bb-ui/react-library/dist/components/SortableTableHeaderCell';
import { TableCell } from '@bb-ui/react-library/dist/components/TableCell';
import { Link } from 'react-router-dom';
import { ChevronRight } from '@bb-ui/icons/dist/small/ChevronRight';
import { HideOffScreen, IconButton, Tooltip, createStyles, makeStyles } from '@bb-ui/react-library';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { MenuItem } from '@bb-ui/react-library/dist/components/MenuItem';
import { LearnSite, LearnInstance, Status } from '@bb-learn-instances-service/common';
import { PaginatedTable } from '../PaginatedTable';
import { LoadingIndicator } from '../LoadingIndicator';
import { ErrorMessage } from '../ErrorMessage';
import { TenantData } from '../../App.types';
import { useAppConfigContext } from '../../contexts/AppConfigContext';
import { useRestApi } from '../../hooks/useRestApi';
import { apiUrl, getBandFromInstanceType, getInstanceClassFromInstanceType } from '../../utils';

export interface InstanceInfo extends LearnInstance {
  SalesforceLicense?: string;
}

const ALL = 'All';
const NONE = '__none__'; // sentinel for tenants with no region / no type

export const isSupportedRegion = (region?: string, supportedRegions: string[] = []): boolean =>
  region !== undefined && supportedRegions.includes(region);

export const getCurrentLearnInstance = (items?: InstanceInfo[]): InstanceInfo | undefined => {
  const current = items?.filter((item) => item.FleetId !== 'legacy');
  return current?.find((item) => item.Status === Status.INSTALLED) ?? current?.[0];
};

// Only sortable when instanceInfoByTenantId makes their data available for every row up front -
// see ColumnHeaderProps.usingBulkInstanceInfo.
const INSTANCE_INFO_SORTABLE_COLUMNS: TenantListColumn[] = [
  'installationId',
  'url',
  'learnVersion',
  'band',
  'instanceClass',
  'fleet',
  'status',
];

const INSTANCE_INFO_FIELD_BY_COLUMN: Partial<
  Record<TenantListColumn, 'InstallationId' | 'Url' | 'LearnVersion' | 'FleetId' | 'Status'>
> = {
  installationId: 'InstallationId',
  url: 'Url',
  learnVersion: 'LearnVersion',
  fleet: 'FleetId',
  status: 'Status',
};

// Sort key for instance-info-derived columns. band/instanceClass use their mapped label to match
// what their ColumnDef Cell actually displays, rather than the raw instance type/license string.
const getInstanceInfoSortValue = (
  columnId: TenantListColumn,
  instanceInfo?: InstanceInfo,
): string | undefined => {
  if (columnId === 'band') {
    const band =
      instanceInfo?.Band && (getBandFromInstanceType(instanceInfo.Band) ?? instanceInfo.Band);
    return band?.toLowerCase();
  }
  if (columnId === 'instanceClass') {
    const instanceClass =
      instanceInfo?.SalesforceLicense &&
      getInstanceClassFromInstanceType(instanceInfo.SalesforceLicense);
    return instanceClass?.toLowerCase();
  }
  const field = INSTANCE_INFO_FIELD_BY_COLUMN[columnId];
  return field ? instanceInfo?.[field]?.toLowerCase() : undefined;
};

// Only fetches when `enabled` is true, so rows that don't display installationId/url/learnVersion
// /band/instanceClass/fleet skip the request entirely.
const useInstanceInfo = (tenantId: string, enabled: boolean) => {
  const { data, error, loading } = useRestApi<LearnSite>(
    apiUrl('lct', `tenants/${tenantId}/instances/learnSites`),
    { manual: !enabled },
  );
  return {
    data: getCurrentLearnInstance(data?.items),
    error,
    loading,
  };
};

export const useStyles = makeStyles(() =>
  createStyles({
    enabledCell: {
      width: 128,
    },
    filterSelect: {
      minWidth: 160,
    },
    paddingCell: {
      padding: 1,
    },
  }),
);

export type TenantListColumn =
  | 'installationId'
  | 'url'
  | 'name'
  | 'instanceClass'
  | 'hostname'
  | 'id'
  | 'tenantType'
  | 'learnVersion'
  | 'status'
  | 'band'
  | 'region'
  | 'isHidden'
  | 'fleet';

export interface TenantListTableProps {
  tenants: TenantData[];
  error?: Error;
  loading?: boolean;
  searchText?: string;
  columns?: TenantListColumn[];
  // When provided (see TenantChildren's useInstanceInfoMap), rows read instance info from here
  // instead of fetching it individually, and search()/sort can match against it for the whole
  // list instead of just the currently visible rows.
  instanceInfoByTenantId?: Record<string, InstanceInfo | undefined>;
}

// Loosely typing this, so we can use <ButtonBase> props
const LooseIconButton = IconButton as any;

const ALL_COLUMNS: TenantListColumn[] = [
  'name',
  'hostname',
  'id',
  'tenantType',
  'region',
  'isHidden',
];

interface ColumnHeaderProps {
  classes: Record<'enabledCell' | 'filterSelect', string>;
  // installationId/url/learnVersion/band/instanceClass are only sortable when
  // instanceInfoByTenantId (see useInstanceInfoMap) has made their data available for every row
  // up front - otherwise the table-level sort would have nothing to sort against.
  usingBulkInstanceInfo: boolean;
}

interface ColumnCellProps {
  tenant: TenantData;
  instanceInfo?: InstanceInfo;
}

interface ColumnDef {
  HeaderCell: React.FC<ColumnHeaderProps>;
  Cell: React.FC<ColumnCellProps>;
}

// instanceInfo-derived cells (installationId/url/learnVersion/band/instanceClass) fall back to
// this same '-' placeholder when there's no data for that tenant yet (or at all).
const fallback = (value?: string): string => value ?? '-';

// Shared by every sortable column header, so useTranslation() is only called once for all of them
// instead of once per column.
const SortableHeader: React.FC<{
  id: string;
  columnId: TenantListColumn;
  labelKey: string;
  tooltipKey: string;
  tableCellProps?: { className: string };
}> = ({ id, columnId, labelKey, tooltipKey, tableCellProps }) => {
  const { t } = useTranslation();
  return (
    <SortableTableHeaderCell
      id={id}
      columnId={columnId}
      tooltip={t(tooltipKey)}
      tableCellProps={tableCellProps}
    >
      {t(labelKey)}
    </SortableTableHeaderCell>
  );
};

// Shared by installationId/url/learnVersion/band/instanceClass when instanceInfoByTenantId isn't
// available (see ColumnHeaderProps.usingBulkInstanceInfo) - without it, that data is fetched
// per-row and the table-level sort has nothing to sort against.
const PlainHeader: React.FC<{ id: string; labelKey: string }> = ({ id, labelKey }) => {
  const { t } = useTranslation();
  return (
    <TableCell role="columnheader" id={id}>
      {t(labelKey)}
    </TableCell>
  );
};

// Single source of truth for how each column renders. Both the header row and every body row
// iterate over the same `visibleColumns` array using these definitions, so the two can never
// drift out of alignment with each other - reordering columns is just reordering that array.
const COLUMN_DEFS: Record<TenantListColumn, ColumnDef> = {
  installationId: {
    HeaderCell: ({ usingBulkInstanceInfo }) =>
      usingBulkInstanceInfo ? (
        <SortableHeader
          id="tenant-list-table-header-installationId"
          columnId="installationId"
          labelKey="tenantList.installationId"
          tooltipKey="tenantList.sortByInstallationId"
        />
      ) : (
        <PlainHeader
          id="tenant-list-table-header-installationId"
          labelKey="tenantList.installationId"
        />
      ),
    Cell: ({ instanceInfo }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-installationId">
        {fallback(instanceInfo?.InstallationId)}
      </TableCell>
    ),
  },
  url: {
    HeaderCell: ({ usingBulkInstanceInfo }) =>
      usingBulkInstanceInfo ? (
        <SortableHeader
          id="tenant-list-table-header-url"
          columnId="url"
          labelKey="tenantList.url"
          tooltipKey="tenantList.sortByUrl"
        />
      ) : (
        <PlainHeader id="tenant-list-table-header-url" labelKey="tenantList.url" />
      ),
    Cell: ({ instanceInfo }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-url">
        {fallback(instanceInfo?.Url)}
      </TableCell>
    ),
  },
  name: {
    HeaderCell: () => (
      <SortableHeader
        id="tenant-list-table-header-name"
        columnId="name"
        labelKey="tenantList.name"
        tooltipKey="tenantList.sortByName"
      />
    ),
    Cell: ({ tenant }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-name">
        {tenant.name}
      </TableCell>
    ),
  },
  instanceClass: {
    HeaderCell: ({ usingBulkInstanceInfo }) =>
      usingBulkInstanceInfo ? (
        <SortableHeader
          id="tenant-list-table-header-instanceClass"
          columnId="instanceClass"
          labelKey="tenantList.instanceClass"
          tooltipKey="tenantList.sortByInstanceClass"
        />
      ) : (
        <PlainHeader
          id="tenant-list-table-header-instanceClass"
          labelKey="tenantList.instanceClass"
        />
      ),
    Cell: ({ instanceInfo }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-instanceClass">
        {fallback(
          instanceInfo?.SalesforceLicense &&
            getInstanceClassFromInstanceType(instanceInfo.SalesforceLicense),
        )}
      </TableCell>
    ),
  },
  hostname: {
    HeaderCell: () => (
      <SortableHeader
        id="tenant-list-table-header-hostname"
        columnId="hostname"
        labelKey="tenantList.hostname"
        tooltipKey="tenantList.sortByHostname"
      />
    ),
    Cell: ({ tenant }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-hostname">
        {tenant.hostname}
      </TableCell>
    ),
  },
  id: {
    HeaderCell: () => (
      <SortableHeader
        id="tenant-list-table-header-id"
        columnId="id"
        labelKey="tenantList.systemId"
        tooltipKey="tenantList.sortById"
      />
    ),
    Cell: ({ tenant }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-id">
        {tenant.id}
      </TableCell>
    ),
  },
  tenantType: {
    HeaderCell: () => (
      <SortableHeader
        id="tenant-list-table-header-type"
        columnId="tenantType"
        labelKey="tenantList.type"
        tooltipKey="tenantList.sortByType"
      />
    ),
    Cell: ({ tenant }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-type">
        {tenant.tenantType}
      </TableCell>
    ),
  },
  learnVersion: {
    HeaderCell: ({ usingBulkInstanceInfo }) =>
      usingBulkInstanceInfo ? (
        <SortableHeader
          id="tenant-list-table-header-learnVersion"
          columnId="learnVersion"
          labelKey="tenantList.learnVersion"
          tooltipKey="tenantList.sortByLearnVersion"
        />
      ) : (
        <PlainHeader
          id="tenant-list-table-header-learnVersion"
          labelKey="tenantList.learnVersion"
        />
      ),
    Cell: ({ instanceInfo }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-learnVersion">
        {fallback(instanceInfo?.LearnVersion)}
      </TableCell>
    ),
  },
  status: {
    HeaderCell: ({ usingBulkInstanceInfo }) =>
      usingBulkInstanceInfo ? (
        <SortableHeader
          id="tenant-list-table-header-status"
          columnId="status"
          labelKey="tenantList.status"
          tooltipKey="tenantList.sortByStatus"
        />
      ) : (
        <PlainHeader id="tenant-list-table-header-status" labelKey="tenantList.status" />
      ),
    Cell: ({ instanceInfo }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-status">
        {fallback(instanceInfo?.Status)}
      </TableCell>
    ),
  },
  band: {
    HeaderCell: ({ usingBulkInstanceInfo }) =>
      usingBulkInstanceInfo ? (
        <SortableHeader
          id="tenant-list-table-header-band"
          columnId="band"
          labelKey="tenantList.band"
          tooltipKey="tenantList.sortByBand"
        />
      ) : (
        <PlainHeader id="tenant-list-table-header-band" labelKey="tenantList.band" />
      ),
    Cell: ({ instanceInfo }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-band">
        {fallback(
          instanceInfo?.Band && (getBandFromInstanceType(instanceInfo.Band) ?? instanceInfo.Band),
        )}
      </TableCell>
    ),
  },
  region: {
    HeaderCell: () => (
      <SortableHeader
        id="tenant-list-table-header-region"
        columnId="region"
        labelKey="tenantList.region"
        tooltipKey="tenantList.sortByRegion"
      />
    ),
    Cell: ({ tenant }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-region">
        {tenant.region}
      </TableCell>
    ),
  },
  isHidden: {
    HeaderCell: ({ classes }) => (
      <SortableHeader
        id="tenant-list-table-header-hidden"
        columnId="isHidden"
        labelKey="tenantList.hidden"
        tooltipKey="tenantList.sortByIsHidden"
        tableCellProps={{ className: classes.enabledCell }}
      />
    ),
    Cell: ({ tenant }) => {
      const { t } = useTranslation();
      return (
        <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-hidden">
          {tenant.isHidden ? t('global.yes') : t('global.no')}
        </TableCell>
      );
    },
  },
  fleet: {
    HeaderCell: ({ usingBulkInstanceInfo }) =>
      usingBulkInstanceInfo ? (
        <SortableHeader
          id="tenant-list-table-header-fleet"
          columnId="fleet"
          labelKey="tenantList.fleet"
          tooltipKey="tenantList.sortByFleet"
        />
      ) : (
        <PlainHeader id="tenant-list-table-header-fleet" labelKey="tenantList.fleet" />
      ),
    Cell: ({ instanceInfo }) => (
      <TableCell tabIndex={-1} aria-describedby="tenant-list-table-header-fleet">
        {fallback(instanceInfo?.FleetId)}
      </TableCell>
    ),
  },
};

interface TenantListRowProps {
  tenant: TenantData;
  index: number;
  visibleColumns: TenantListColumn[];
  supportedRegions: string[];
  instanceInfoByTenantId?: Record<string, InstanceInfo | undefined>;
}

// A dedicated component (rather than a plain function called from .map) so each row gets its own
// React fiber - useInstanceInfo is a hook, and calling it from a bare function shared across rows
// would violate the rules of hooks as soon as the row count changes.
const TenantListRow: React.FC<TenantListRowProps> = ({
  tenant,
  index,
  visibleColumns,
  supportedRegions,
  instanceInfoByTenantId,
}) => {
  const { t } = useTranslation();
  const { id, region } = tenant;
  const usingBulkInstanceInfo = instanceInfoByTenantId !== undefined;
  const needsInstanceInfo =
    !usingBulkInstanceInfo &&
    tenant.tenantType === 'Learn' &&
    visibleColumns.some(
      (columnId) =>
        columnId === 'installationId' ||
        columnId === 'learnVersion' ||
        columnId === 'band' ||
        columnId === 'url' ||
        columnId === 'instanceClass' ||
        columnId === 'fleet',
    );
  const { data: fetchedInstanceInfo } = useInstanceInfo(id, needsInstanceInfo);
  const instanceInfo = usingBulkInstanceInfo ? instanceInfoByTenantId[id] : fetchedInstanceInfo;

  return (
    <TableRow key={id} aria-rowindex={index + 1} data-testid={`tenant-list-table-row-${id}`}>
      {visibleColumns.map((columnId) => {
        const { Cell } = COLUMN_DEFS[columnId];
        return <Cell key={columnId} tenant={tenant} instanceInfo={instanceInfo} />;
      })}
      <TableCell tabIndex={-1}>
        <Tooltip
          title={
            !isSupportedRegion(region, supportedRegions)
              ? t('tenantList.unsupportedRegion', { regions: supportedRegions }) || ''
              : ''
          }
        >
          <span>
            <LooseIconButton
              aria-label={t('tenantList.moreInformation')}
              component={Link}
              to={`/tenants/${id}`}
              disabled={!isSupportedRegion(region, supportedRegions)}
              aria-disabled={!isSupportedRegion(region, supportedRegions)}
            >
              <Typography component="span" variant="inherit">
                {t('global.view')}
              </Typography>
              <ChevronRight />
            </LooseIconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export const TenantListTable: React.FC<TenantListTableProps> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles(props);
  const supportedRegions = useAppConfigContext().tenantSupportedRegions ?? [];
  const visibleColumns = props.columns ?? ALL_COLUMNS;
  const usingBulkInstanceInfo = props.instanceInfoByTenantId !== undefined;

  // FILTER STATE
  const [selectedRegion, setSelectedRegion] = React.useState<string>(ALL);
  const [selectedTenantType, setSelectedTenantType] = React.useState<string>(ALL);
  const [selectedStatus, setSelectedStatus] = React.useState<string>(ALL);

  // Derive dropdown options from ALL tenants (unaffected by current filter selection)
  const availableRegions = React.useMemo<string[]>(
    () =>
      Array.from(
        new Set(props.tenants.map((t) => t.region).filter((r): r is string => !!r)),
      ).sort(),
    [props.tenants],
  );

  const hasTenantsWithNoRegion = React.useMemo(
    () => props.tenants.some((t) => !t.region),
    [props.tenants],
  );

  const availableTenantTypes = React.useMemo<string[]>(
    () =>
      Array.from(
        new Set(props.tenants.map((t) => t.tenantType).filter((tt): tt is string => !!tt)),
      ).sort(),
    [props.tenants],
  );

  const hasTenantsWithNoType = React.useMemo(
    () => props.tenants.some((t) => !t.tenantType),
    [props.tenants],
  );

  const availableStatuses = React.useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          props.instanceInfoByTenantId
            ? Object.values(props.instanceInfoByTenantId)
                .map((info) => info?.Status)
                .filter((status): status is Status => !!status)
            : [],
        ),
      ).sort(),
    [props.instanceInfoByTenantId],
  );

  const hasTenantsWithNoStatus = React.useMemo(
    () => props.tenants.some((tenant) => !props.instanceInfoByTenantId?.[tenant.id]?.Status),
    [props.tenants, props.instanceInfoByTenantId],
  );

  // Apply region / type / status filter
  const filteredTenants = React.useMemo<TenantData[]>(
    () =>
      props.tenants.filter((tenant) => {
        if (selectedRegion === NONE && !!tenant.region) return false;
        if (selectedRegion !== ALL && selectedRegion !== NONE && tenant.region !== selectedRegion)
          return false;
        if (selectedTenantType === NONE && !!tenant.tenantType) return false;
        if (
          selectedTenantType !== ALL &&
          selectedTenantType !== NONE &&
          tenant.tenantType !== selectedTenantType
        )
          return false;
        const status = props.instanceInfoByTenantId?.[tenant.id]?.Status;
        if (selectedStatus === NONE && !!status) return false;
        return !(selectedStatus !== ALL && selectedStatus !== NONE && status !== selectedStatus);
      }),
    [
      props.tenants,
      selectedRegion,
      selectedTenantType,
      selectedStatus,
      props.instanceInfoByTenantId,
    ],
  );

  // TEXT SEARCH
  // installationId/url/learnVersion/band/instanceClass/fleet are only searchable when the caller
  // passes instanceInfoByTenantId (see useInstanceInfoMap) - otherwise that data is fetched
  // per-row only when its column is visible, so it isn't available on TenantData for the full
  // tenant list to search against.
  const search =
    (query: string) =>
    ({ id, name, hostname }: TenantData) => {
      const q = query.toLowerCase();
      const instanceInfo = props.instanceInfoByTenantId?.[id];
      return (
        (name?.toLowerCase().includes(q) ?? false) ||
        (id?.includes(query) ?? false) ||
        (hostname?.includes(q) ?? false) ||
        (instanceInfo?.InstallationId?.toLowerCase().includes(q) ?? false) ||
        (instanceInfo?.Url?.toLowerCase().includes(q) ?? false) ||
        (instanceInfo?.LearnVersion?.toLowerCase().includes(q) ?? false) ||
        (instanceInfo?.FleetId?.toLowerCase().includes(q) ?? false) ||
        (getInstanceInfoSortValue('band', instanceInfo)?.includes(q) ?? false) ||
        (getInstanceInfoSortValue('instanceClass', instanceInfo)?.includes(q) ?? false)
      );
    };

  // SORTING (applied on top of filtered data)
  const [sortParams, setSortParams] = React.useState<Partial<OnSortChangedParams>>({});
  const sortedTenants = React.useMemo(() => {
    const { sortColumnId, sortDirection } = sortParams;
    if (sortColumnId && ['name', 'hostname', 'id', 'region', 'tenantType'].includes(sortColumnId)) {
      return orderBy(
        filteredTenants,
        [(tenant) => get(tenant, sortColumnId)?.toLowerCase()],
        sortDirection,
      );
    }
    if (sortColumnId === 'isHidden') {
      return orderBy(filteredTenants, [(tenant) => tenant.isHidden], sortDirection);
    }
    if (
      props.instanceInfoByTenantId &&
      INSTANCE_INFO_SORTABLE_COLUMNS.includes(sortColumnId as TenantListColumn)
    ) {
      return orderBy(
        filteredTenants,
        [
          (tenant) =>
            getInstanceInfoSortValue(
              sortColumnId as TenantListColumn,
              props.instanceInfoByTenantId?.[tenant.id],
            ),
        ],
        sortDirection,
      );
    }
    return filteredTenants;
  }, [sortParams, filteredTenants, props.instanceInfoByTenantId]);

  const getAriaSortMessage = (columnId?: string, sortDirection?: SortDirection) => {
    const columnLabel = t(`tenantList.${columnId}`);
    const orderLabel =
      sortDirection === 'asc'
        ? t('global.paginatedTable.ascending')
        : t('global.paginatedTable.descending');
    return t('global.paginatedTable.sortedAriaMessage', { columnLabel, orderLabel });
  };

  // Filter selects rendered as toolbarStart inside PaginatedTable's toolbar row
  const filterSelects = (
    <>
      <TextField
        select
        id="tenant-region-select"
        label={t('tenantList.filterByRegion')}
        value={selectedRegion}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedRegion(e.target.value)}
        className={classes.filterSelect}
        InputLabelProps={{ className: classes.paddingCell }}
        floatingLabel
      >
        <MenuItem value={ALL}>{t('tenantList.allRegions')}</MenuItem>
        {hasTenantsWithNoRegion && <MenuItem value={NONE}>{t('tenantList.noRegion')}</MenuItem>}
        {availableRegions.map((region) => (
          <MenuItem key={region} value={region}>
            {region}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        id="tenant-type-select"
        label={t('tenantList.filterByType')}
        value={selectedTenantType}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedTenantType(e.target.value)}
        className={classes.filterSelect}
        InputLabelProps={{ className: classes.paddingCell }}
        floatingLabel
      >
        <MenuItem value={ALL}>{t('tenantList.allTypes')}</MenuItem>
        {hasTenantsWithNoType && <MenuItem value={NONE}>{t('tenantList.noType')}</MenuItem>}
        {availableTenantTypes.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        id="tenant-status-select"
        label={t('tenantList.filterByStatus')}
        value={selectedStatus}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedStatus(e.target.value)}
        className={classes.filterSelect}
        InputLabelProps={{ className: classes.paddingCell }}
        floatingLabel
      >
        <MenuItem value={ALL}>{t('tenantList.allStatuses')}</MenuItem>
        {hasTenantsWithNoStatus && <MenuItem value={NONE}>{t('tenantList.noStatus')}</MenuItem>}
        {availableStatuses.map((status) => (
          <MenuItem key={status} value={status}>
            {status}
          </MenuItem>
        ))}
      </TextField>
    </>
  );

  let content: React.ReactElement;

  if (props.error) {
    content = (
      <ErrorMessage
        data-testid="fnds-tenant-list-error"
        title={t('tenantList.loadError')}
        message={props.error.message}
        variant="block"
      />
    );
  } else if (props.loading) {
    content = <LoadingIndicator data-testid="fnds-tenant-list-init" />;
  } else if (props.tenants.length === 0) {
    content = (
      <Typography data-testid="fnds-tenant-list-no-data">{t('tenantList.noData')}</Typography>
    );
  } else {
    content = (
      <PaginatedTable
        onSortChanged={(sortParams) => {
          setSortParams(sortParams);
          return true;
        }}
        getSortChangedAriaMessage={getAriaSortMessage}
        toolbarStart={filterSelects}
        searchBoxProps={{ label: props.searchText }}
        searchHelperText={t('tenantList.searchHelperText')}
        sortedData={sortedTenants}
        search={search}
        noMatchesMessage={(searchExpression) => t('tenantList.noMatch', { searchExpression })}
        renderHead={() => (
          <TableHead>
            <TableRow>
              {visibleColumns.map((columnId) => {
                const { HeaderCell } = COLUMN_DEFS[columnId];
                return (
                  <HeaderCell
                    key={columnId}
                    classes={classes}
                    usingBulkInstanceInfo={usingBulkInstanceInfo}
                  />
                );
              })}
              <TableCell role="columnheader">
                <HideOffScreen>Actions</HideOffScreen>
              </TableCell>
            </TableRow>
          </TableHead>
        )}
        renderRow={(tenant, index) => (
          <TenantListRow
            key={tenant.id}
            tenant={tenant}
            index={index}
            visibleColumns={visibleColumns}
            supportedRegions={supportedRegions}
            instanceInfoByTenantId={props.instanceInfoByTenantId}
          />
        )}
      />
    );
  }

  return content;
};
